import { useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mp_fit_profile_v1";

/**
 * useFitProfile — reads the visitor's body measurements from localStorage and
 * keeps them in state. Returns null when nothing is saved. Reactive across tabs
 * via the 'storage' event so opening /shop after saving a profile in another
 * tab picks it up immediately.
 */
export function useFitProfile() {
  const [profile, setProfile] = useState(null);

  const readFromStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setProfile(null); return; }
      const parsed = JSON.parse(raw);
      const clean = {};
      for (const [k, v] of Object.entries(parsed || {})) {
        if (v !== null && v !== undefined && !isNaN(Number(v)) && Number(v) > 0) clean[k] = Number(v);
      }
      setProfile(Object.keys(clean).length > 0 ? clean : null);
    } catch (e) { /* noop — Safari private mode etc. */ }
  };

  useEffect(() => {
    readFromStorage();
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) readFromStorage();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return profile;
}

/**
 * scoreItemAgainstProfile — compute the average absolute delta (in cm) between
 * the piece's length fields and the visitor's profile, considering ONLY
 * overlapping fields.
 *
 * Resilience strategy:
 *  - When category_schema is present we honour the unit_kind="length" filter
 *    (so shoe-size keys like "eu_size" don't get treated as cm).
 *  - When category_schema is absent (e.g. list endpoints that omit it), we
 *    fall back to scoring any numeric key both sides agree on. Length-typed
 *    measurements are the only ones written through the typed inputs anyway,
 *    so the fallback is safe for the dominant case.
 *
 * Returns null when there's no overlap — those items keep their natural order
 * and never display a "your fit" ribbon.
 */
export function scoreItemAgainstProfile(item, profile) {
  if (!item || !profile) return null;
  const map = item.measurements_map || {};
  const schema = item.category_schema;
  const allowedKeys = (schema && schema.length)
    ? new Set(schema.filter((f) => f.unit_kind === "length").map((f) => f.key))
    : null; // null = no schema-side filter; fall back to numeric overlap
  const deltas = [];
  for (const [key, youVal] of Object.entries(profile)) {
    if (allowedKeys && !allowedKeys.has(key)) continue;
    const itemVal = map[key];
    if (itemVal === undefined || itemVal === null || itemVal === "") continue;
    const itemNum = Number(itemVal);
    const youNum = Number(youVal);
    if (isNaN(itemNum) || isNaN(youNum)) continue;
    deltas.push(Math.abs(itemNum - youNum));
  }
  if (deltas.length === 0) return null;
  const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length;
  return { avgDelta: avg, fieldCount: deltas.length };
}

/**
 * useFitRanking — sorts items so pieces closest to the visitor's body sit at
 * the top, while preserving the original order for items that can't be scored.
 *
 * Returns {items, scores: Map<id, score>, hasProfile, hasFitMatches}.
 * - When hasProfile is false, items are returned unchanged and scores is empty.
 * - "Match" threshold: items within ~5 cm average delta get a ribbon flag.
 */
const RIBBON_THRESHOLD_CM = 5.0;

export function useFitRanking(items) {
  const profile = useFitProfile();

  return useMemo(() => {
    if (!profile || !items || items.length === 0) {
      return { items: items || [], scores: new Map(), hasProfile: !!profile, hasFitMatches: false };
    }
    const scored = items.map((it, i) => ({ it, i, score: scoreItemAgainstProfile(it, profile) }));
    // Two buckets — scored pieces first (sorted asc by avgDelta), then unscored in original order.
    const withScore = scored.filter((x) => x.score !== null).sort((a, b) => a.score.avgDelta - b.score.avgDelta);
    const withoutScore = scored.filter((x) => x.score === null).sort((a, b) => a.i - b.i);
    const ordered = [...withScore.map((x) => x.it), ...withoutScore.map((x) => x.it)];
    const scoreMap = new Map();
    for (const { it, score } of scored) {
      if (score) scoreMap.set(it.id, score);
    }
    const hasFitMatches = withScore.some((x) => x.score.avgDelta <= RIBBON_THRESHOLD_CM);
    return { items: ordered, scores: scoreMap, hasProfile: true, hasFitMatches };
  }, [items, profile]);
}

export { RIBBON_THRESHOLD_CM };
